import env from '~/libs/env'
import { get } from 'es-toolkit/compat'
import { useRootStore } from '~/store/root'
import { useUserStore } from '~/store/user'
import { useIdentityStore } from '~/store/identity';

const _passthruHeaders = [
  'x-forwarded-for',
  'cf-connecting-ip',
  'user-agent',
  'cf-ray',
  'cf-ipcountry',
  'cf-ipcity',
  'cf-region',
  'cf-postal-code',
  'cf-timezone',
  'cf-ja4',
]

function _isValidDomain(string) {
  // ^(https?:\/\/)?: Allows the string to optionally start with http:// or https://.
  // ([a-zA-Z0-9-_]+\.)*: Matches subdomains (e.g., www. or subdomain.), which can occur zero or more times.
  // [a-zA-Z0-9][a-zA-Z0-9-_]+: Matches the main domain part, ensuring it starts with an alphanumeric character and can contain alphanumeric characters and hyphens.
  // \.[a-zA-Z]{2,11}?$: Matches the top-level domain (TLD), which must start with a dot and be between 2 and 11 alphabetic characters long.
  const validDomainStructure = /^(https?:\/\/)?([a-zA-Z0-9-_]+\.)*[a-zA-Z0-9][a-zA-Z0-9-_]+\.[a-zA-Z]{2,11}?$/;
  return env('environment') === 'dev' || validDomainStructure.test(string)
}

function _getLendioHosts() {
  return ['apiUrl', 'bpApiUrl'].map(url => {
    const value = env(url)
    if (!value || !/^(https?:)?\/\//.test(value)) return null
    try {
      return new URL(value, window.location.origin).host
    } catch (e) {
      return null
    }
  }).filter(url => !!url)
}

async function _handle401 ({ error, isEmbedded, logoutFn }) {
  if (isEmbedded) {
    throw createError({
      statusCode: 401,
      statusMessage: 'Unauthorized Action',
      data: { isEmbedded: true, location: 'Axios' },
      fatal: true
    })
  }

  const logoutRedirect = get(error, 'response.data.errors[0].message') == 'User is marked as do not contact'
    ? '/under-review'
    : null
  await logoutFn(logoutRedirect)
  return Promise.reject(error)
}

function _addAuthHeader(getAuthHeaderFn) {
  return function(config) {
    if (config.headers['Authorization']) {
      return config
    }

    const authHeader = getAuthHeaderFn({
      acceptNoAuth: true,
      requestUrl: config?.url ?? null
    })

    if (authHeader) {
      config.headers['Authorization'] = authHeader
    }

    return config
  }
}

function _handleSuccessResponse(reauthFn) {
  return async (response) => {
    const shouldNotIntercept = (
      !response ||
      response?.config?.url?.includes('/auth/token-refresh') ||
      !response?.config?.headers['Authorization']
    )

    if (shouldNotIntercept || !response.config?.url) {
      return response
    }

    try {
      if (_getLendioHosts().includes(new URL(response.config.url, window.location.origin).host)) {
        await reauthFn()
      }
    } catch (e) {
      if (!(e instanceof TypeError)) {
        // Propagate non-TypeErrors
        throw e
      }
      // Handle invalid URL gracefully - just skip reauth
    }

    return response
  }
}

function _handleErrorResponse(logoutFn, isEmbedded) {
  let inProgress401 = false

  return async (error) => {
    if (!error?.response && error?.code !== 'ERR_CANCELED') {
      log.warning('Axios Network Error: ', error)
    }

    if (env('environment') != 'LIVE') {
      console.log('AXIOS error on', get(error, 'response.request.path'), error.stack)
    }

    const requestConfig = error.config || error.response?.config

    if (
      error?.request?.responseURL?.includes(env('identityServiceUrl')) ||
      !requestConfig?.headers?.['Authorization'] ||
      error?.response?.status !== 401 ||
      inProgress401
    ) {
      return Promise.reject(error)
    }

    inProgress401 = true

    return _handle401({ error, isEmbedded, logoutFn }).finally(() => { inProgress401 = false })
  }
}

function configureAxios({ $axios, $axiosWithRetry, $pinia, $lendioCookies }) {
  $pinia.use(() => ({ $axios, $axiosWithRetry }))
  const userStore = useUserStore()
  const identityStore = useIdentityStore()
  const rootStore = useRootStore()
  const route = useRoute()

  // auth header interceptor
  const authHeaderInterceptor = _addAuthHeader(userStore.getAuthHeader)
  $axios.interceptors.request.use(authHeaderInterceptor)
  $axiosWithRetry.interceptors.request.use(authHeaderInterceptor)

  // success and error response interceptors
  const successResponseInterceptor = _handleSuccessResponse(userStore.reauth)
  const isEmbedded = (rootStore.isEmbedded || rootStore.authUser?.isEmbedded || $lendioCookies.get('isEmbedded') || route.path.includes('embedded'))
  const errorResponseInterceptor = _handleErrorResponse(identityStore.logout, isEmbedded)
  $axios.interceptors.response.use(successResponseInterceptor, errorResponseInterceptor)
  $axiosWithRetry.interceptors.response.use(successResponseInterceptor, errorResponseInterceptor)
}

export default defineNuxtPlugin({ name: 'axiosHandler', dependsOn: ['axiosSetup'], setup: configureAxios })

// Export utility functions for testing
export {
  _addAuthHeader,
  _handleErrorResponse,
  _getLendioHosts,
  _handle401,
  _isValidDomain,
  _passthruHeaders,
  _handleSuccessResponse,
}
